import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  archiveCampaign,
  fetchCampaignList,
  pauseCampaign,
  patchCampaign,
  resumeCampaign,
} from '../api';

export function useCampaignList(filters?: { period?: string; status?: string; page?: number }) {
  return useGrowthQuery({
    queryKey: growthKeys.campaigns.list(filters ?? {}),
    queryFn: () => fetchCampaignList(filters),
  });
}

export function useCampaignMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'campaigns'] });

  const toggleActive = useMutation({
    mutationFn: async ({ id, nextActive, backendId }: { id: number; nextActive: boolean; backendId: string }) => {
      if (nextActive) return resumeCampaign(backendId);
      return pauseCampaign(backendId);
    },
    onSuccess: () => {
      notify.success('Campaign updated');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const archive = useMutation({
    mutationFn: archiveCampaign,
    onSuccess: () => {
      notify.success('Campaign archived');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const updateBudget = useMutation({
    mutationFn: ({ id, budgetCents }: { id: string; budgetCents: number }) =>
      patchCampaign(id, { monthly_budget_cents: budgetCents }),
    onSuccess: () => {
      notify.success('Budget updated');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  return { toggleActive, archive, updateBudget };
}
